import { useState } from 'react';
import { Loader2, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-table';
import { formatApiDate } from '@Utils';

import { Badge, BadgeVariant, SupplierAddonsModal, SupplierPlanModal, Table } from '@Components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@Components/ui-v2/dropdown-menu';
import {
  ACCOUNT_PENDING_REFRESH,
  ACCOUNT_READY,
  ACCOUNT_WITHOUT_DATA,
  CREATION_IN_PROGRESS,
  DataSupplier,
  PlanDashboardItem,
} from '@Models';
import config from '../../config';
import {
  GS1_PLAN_NAME_INVENTARIO_INTELIGENTE_RETAILER,
  GS1_PLAN_NAME_SMB,
} from '../SupplierModals/constants/gs1PlanNames';
import { getSupplierColumns, SupplierColumnId } from './columns';
import { getConnectorLabel, getStatusLabel } from './labels';

interface SupplierListProps {
  suppliers: DataSupplier[];
  canManage: boolean;
  onSupplierUpdated: () => void;
}

// The comparator shape react-table's `sortType` column option takes. Named so
// the column definitions can carry it as a type instead of `unknown`, which is
// not spreadable into the column object.
type SupplierSortFn = (rowA: Row<DataSupplier>, rowB: Row<DataSupplier>) => number;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  [ACCOUNT_READY]: 'green',
  [CREATION_IN_PROGRESS]: 'yellow',
  [ACCOUNT_WITHOUT_DATA]: 'gray',
  [ACCOUNT_PENDING_REFRESH]: 'blue',
};

const SupplierList = ({ suppliers, canManage, onSupplierUpdated }: SupplierListProps) => {
  const { t, i18n } = useTranslation();
  const [planModalSupplier, setPlanModalSupplier] = useState<DataSupplier | null>(null);
  const [addonsModalSupplier, setAddonsModalSupplier] = useState<DataSupplier | null>(null);

  // Add-ons are Logyca-only (GS1 has no plan+partition-scoped add-ons catalog —
  // its extra dashboards are custom_dashboard_ids instead). Read from the build
  // flavor rather than sniffed from the payload: the previous
  // `suppliers.some(s => !!s.plan_id)` check meant "is Logyca" only for as long
  // as GS1's resolver left plan_id null, and silently stopped being true when
  // it started populating it.
  const showAddons = config.isLogycaFlavor;

  const getStatus = (status: string) => getStatusLabel(status, t);

  const levelOrder: Record<string, number> = {
    [CREATION_IN_PROGRESS]: 1,
    [ACCOUNT_PENDING_REFRESH]: 2,
    [ACCOUNT_READY]: 3,
    [ACCOUNT_WITHOUT_DATA]: 4,
  };

  // TODO: move this ordering to the supplier resolvers in a follow-up PR and
  // delete this block — the default order should arrive sorted instead of the
  // client reordering the list. react-table's column sorting is unaffected: it
  // overrides the row order on click and falls back to the order the data
  // arrived in.
  //
  // Migrated Logyca clients carry no status. They sort last, and the fallback
  // says so rather than leaving it to how `undefined` happens to compare.
  const NO_STATUS_ORDER = 99;
  const statusOrder = (supplier: DataSupplier): number =>
    levelOrder[supplier?.configuration?.status ?? ''] ?? NO_STATUS_ORDER;

  // Copied before sorting: `sort` works in place, and this array is the prop
  // the page owns — the same reference it holds as `suppliers` whenever no
  // search filter is applied.
  const supplierSorted = [...suppliers].sort((a, b) => statusOrder(a) - statusOrder(b));

  const buildBadge = (color: BadgeVariant, label: string, toolInfo: string | null = null, icon?: React.ReactNode) => {
    return <Badge variant={color} label={label} tooltipId={label} toolInfo={toolInfo ?? undefined} icon={icon} />;
  };

  // Only the colour lives here — the labels come from `labels.ts` so the CSV
  // export renders the same retailer names.
  const CONNECTOR_VARIANTS: Record<string, BadgeVariant> = {
    gs1_wrl: 'blue',
    soriana: 'green',
  };

  const buildBadges = (values: string[]): JSX.Element => {
    return (
      <div className="flex gap-2">
        {values?.map((value: string) => (
          // Keyed on the slug: it is unique per supplier (a supplier has at
          // most one credential per connector type), so it survives reordering
          // in a way an index would not.
          <span key={value}>{buildBadge(CONNECTOR_VARIANTS[value] ?? 'gray', getConnectorLabel(value))}</span>
        ))}
      </div>
    );
  };

  const getPlanVariant = (supplier: DataSupplier): BadgeVariant => {
    // By slug, not the display label (GS1's SMB plan display_name is
    // "SMBs", seeded by *_seed_gs1_plans_and_dashboards.sql) -- immune to a
    // future rewording, same as the retailers-plan check below.
    // supplier.plan_name is only ever set for GS1 suppliers (Logyca's
    // resolver never populates it), so this is always 'brand' for Logyca.
    return supplier.plan_name === GS1_PLAN_NAME_SMB ? 'orange' : 'brand';
  };

  const buildAddonsCell = (addons: PlanDashboardItem[], supplier: DataSupplier): JSX.Element => {
    if (addons.length === 0) return <div>-</div>;

    const content =
      addons.length <= 3 ? (
        <div className="flex gap-1 flex-wrap">
          {addons.map((addon) => (
            <span key={addon.id}>{buildBadge('gray', addon.dashboard_name)}</span>
          ))}
        </div>
      ) : (
        buildBadge('brand', t('Supplier.AddonsCount', { count: addons.length }))
      );

    if (!canManage || !showAddons) return content;

    return (
      <button type="button" className="cursor-pointer" onClick={() => setAddonsModalSupplier(supplier)}>
        {content}
      </button>
    );
  };

  const buildPlanCell = (plan: string, supplier: DataSupplier): JSX.Element => {
    if (!plan) return <div />;
    const badge = buildBadge(getPlanVariant(supplier), plan);
    const isRetailersPlan = supplier.plan_name === GS1_PLAN_NAME_INVENTARIO_INTELIGENTE_RETAILER;
    if (!canManage || !supplier.plan_id || isRetailersPlan) return badge;

    return (
      <button type="button" className="cursor-pointer" onClick={() => setPlanModalSupplier(supplier)}>
        {badge}
      </button>
    );
  };

  const buildActionsCell = (supplier: DataSupplier): JSX.Element => {
    if (!canManage || !supplier.plan_id) return <div />;
    const isRetailersPlan = supplier.plan_name === GS1_PLAN_NAME_INVENTARIO_INTELIGENTE_RETAILER;
    if (isRetailersPlan && !showAddons) return <div />;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('Supplier.RowActions')}
            className="inline-flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-200/80 hover:text-gray-900 focus:outline-none">
            <MoreVertical size={16} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          {!isRetailersPlan && (
            <DropdownMenuItem onSelect={() => setPlanModalSupplier(supplier)} className="cursor-pointer">
              {t('Supplier.UpdatePlan')}
            </DropdownMenuItem>
          )}
          {showAddons && (
            <DropdownMenuItem onSelect={() => setAddonsModalSupplier(supplier)} className="cursor-pointer">
              {t('Supplier.ManageAddons')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const builderCell = (type: string, accessor: string, value, supplier: DataSupplier): JSX.Element => {
    if (accessor === 'connectors') {
      if (!value) return <div />;
      return buildBadges(value);
    }
    if (type === 'badge-status') {
      // Clients migrated into Logyca rather than created through the platform
      // have no status. A dash reads as "not recorded"; an empty badge reads
      // as a rendering bug.
      if (!value) return <div>—</div>;
      const variant = STATUS_VARIANT[value] ?? 'gray';
      const icon = value === ACCOUNT_PENDING_REFRESH ? <Loader2 className="size-3.5 animate-spin" /> : undefined;
      return buildBadge(variant, getStatus(value).label, getStatus(value).tooltip, icon);
    }
    if (type === 'badge-plan') {
      return buildPlanCell(value, supplier);
    }
    if (type === 'addons') {
      return buildAddonsCell(value ?? [], supplier);
    }
    if (type === 'actions') {
      return buildActionsCell(supplier);
    }
    if (type === 'date') {
      return <div className="whitespace-nowrap">{formatApiDate(value, i18n.language)}</div>;
    }
    return <div key={accessor}>{value}</div>;
  };

  const getValueCell = (row: Row<DataSupplier>, key: string): string => {
    let cellValue = row?.original[key];
    if (key === 'configuration.status') cellValue = row?.original?.configuration?.status;
    if (key === 'configuration.connectors') cellValue = row?.original?.connectors;
    return cellValue;
  };

  // ISO-8601 strings order correctly as plain strings, so no Date parsing is
  // needed. A missing date compares as the oldest value; the column sets
  // `sortDescFirst` so the first click lands on newest-first and leaves those
  // rows at the bottom.
  const dateSort = (rowA: Row<DataSupplier>, rowB: Row<DataSupplier>): number => {
    const a = rowA.original.created_at ?? '';
    const b = rowB.original.created_at ?? '';
    if (a === b) return 0;
    return a > b ? 1 : -1;
  };

  // Orders by how many credentials a supplier has. `connectors` is nullable, so
  // it is defaulted before reading .length. The result is not negated for
  // `desc`: react-table inverts a comparator's output itself, and doing it here
  // too makes both directions produce the same order.
  const customSort = (rowA: Row<DataSupplier>, rowB: Row<DataSupplier>): number => {
    const a = rowA.original.connectors?.length ?? 0;
    const b = rowB.original.connectors?.length ?? 0;

    if (a > b) return 1;
    if (b > a) return -1;
    return 0;
  };

  const getColumns = () => {
    // What each column *is*. Which ones render, and in what order, lives in
    // columns.ts — keeping the two apart is what stops a column from being
    // gated in one place while something else assumes it exists.
    const definitions: Record<
      SupplierColumnId,
      {
        label: string;
        accessor: string;
        key: string;
        type: string;
        sortType?: SupplierSortFn;
        sortDescFirst?: boolean;
      }
    > = {
      gln: { label: 'GLN', accessor: 'gln', key: 'gln', type: 'string' },
      name: { label: t('Supplier.Name'), accessor: 'display_name', key: 'display_name', type: 'string' },
      status: { label: t('Supplier.Status'), accessor: 'status', key: 'configuration.status', type: 'badge-status' },
      plan: { label: t('Supplier.Plan'), accessor: 'plan', key: 'plan', type: 'badge-plan' },
      addons: {
        label: t('Supplier.AddonsColumn'),
        accessor: 'enabled_addons',
        key: 'enabled_addons',
        type: 'addons',
      },
      connectors: {
        label: t('Supplier.Connectors'),
        accessor: 'connectors',
        key: 'configuration.connectors',
        type: 'string',
        sortType: customSort,
      },
      createdAt: {
        label: t('Supplier.CreatedAt'),
        accessor: 'created_at',
        key: 'created_at',
        type: 'date',
        sortType: dateSort,
        sortDescFirst: true,
      },
      // Header intentionally blank — the column is just the per-row ⋮ menu.
      actions: { label: '', accessor: 'actions', key: 'actions', type: 'actions' },
    };

    return getSupplierColumns().map((id) => {
      const column = definitions[id];
      return {
        Header: column.label,
        accessor: column.accessor,
        Cell: ({ row }) => {
          const cellValue = getValueCell(row, column.key);
          return builderCell(column.type, column.accessor, cellValue, row.original);
        },
        ...(column.sortType && { sortType: column.sortType }),
        ...(column.sortDescFirst && { sortDescFirst: column.sortDescFirst }),
      };
    });
  };

  return (
    <div className="flex mt-[10px]">
      <Table columns={getColumns()} data={supplierSorted} pageSize={9} pagination />

      {planModalSupplier && (
        <SupplierPlanModal
          supplier={planModalSupplier}
          onClose={() => setPlanModalSupplier(null)}
          onUpdated={() => {
            setPlanModalSupplier(null);
            onSupplierUpdated();
          }}
        />
      )}

      {addonsModalSupplier && (
        <SupplierAddonsModal
          supplier={addonsModalSupplier}
          onClose={() => setAddonsModalSupplier(null)}
          onUpdated={() => {
            setAddonsModalSupplier(null);
            onSupplierUpdated();
          }}
        />
      )}
    </div>
  );
};

export default SupplierList;
